/**
 * Offscreen canvas tint system.
 *
 * Applies a color overlay to sprite images using Canvas 2D compositing:
 * 1. Draw the original sprite onto a temporary offscreen canvas.
 * 2. Set globalCompositeOperation = 'source-atop'.
 * 3. Fill with the tint color at the given alpha.
 * 4. Return the tinted canvas (cached).
 *
 * Cache is keyed by source image identity + quantized tint params so that
 * pulsing alpha values don't create unlimited cache entries.
 */

const ALPHA_STEP = 0.05;

function quantize(value: number, step: number): number {
	return Math.round(value / step) * step;
}

/** Map from source image -> Map from cache key string -> tinted canvas */
const cache = new WeakMap<HTMLImageElement | HTMLCanvasElement, Map<string, HTMLCanvasElement>>();

function makeCacheKey(r: number, g: number, b: number, a: number): string {
	return `${r},${g},${b},${quantize(a, ALPHA_STEP).toFixed(2)}`;
}

export function getTintedImage(
	source: HTMLImageElement | HTMLCanvasElement,
	r: number,
	g: number,
	b: number,
	a: number,
): HTMLCanvasElement | null {
	if (!source) return null;

	const w = source.width;
	const h = source.height;
	if (w === 0 || h === 0) return null;

	const quantizedAlpha = quantize(a, ALPHA_STEP);
	const key = makeCacheKey(r, g, b, quantizedAlpha);

	let sourceCache = cache.get(source);
	if (!sourceCache) {
		sourceCache = new Map();
		cache.set(source, sourceCache);
	}

	const cached = sourceCache.get(key);
	if (cached) return cached;

	const offscreen = document.createElement("canvas");
	offscreen.width = w;
	offscreen.height = h;
	const offCtx = offscreen.getContext("2d")!;

	// Draw original sprite
	offCtx.drawImage(source, 0, 0);

	// Apply tint over the existing pixels only (source-atop)
	offCtx.globalCompositeOperation = "source-atop";
	offCtx.fillStyle = `rgba(${r}, ${g}, ${b}, ${quantizedAlpha})`;
	offCtx.fillRect(0, 0, w, h);

	sourceCache.set(key, offscreen);
	return offscreen;
}
